// 권역 관리 레이어 — 권역 경계(bbox 사각형) + 권역 라벨 마커.
//
// gcare(광주형 통합돌봄) 지역지도의 "권역 관리" 레이어를 이식한 것.
// 원본은 Leaflet + 5개 구(區) 하드코딩 폴리곤이었지만, 여기서는 권역이 17개
// 시도(+파일럿)라 하드코딩 대신 백엔드(/api/v1/map/regions)가 하위 좌표에서
// 뽑아준 bbox를 그대로 쓴다.
//
// 시각 규칙은 overlays.ts의 하위구역 폴리곤과 같은 계열:
//   default  — 얇은 점선 + 아주 옅은 fill (아무것도 선택 안 했을 때 전 권역)
//   selected — 굵은 실선 + 진한 fill (그 권역만 도드라짐)
//   muted    — 거의 투명 (다른 권역이 선택됐을 때 시선 뺏지 않기)

import * as React from "react";
import type { BBox, Coord } from "./types";

export interface MapRegion {
  id: number;
  code: string;
  name: string;
  area_description?: string | null;
  manager_name?: string | null;
  operation_status?: string | null;
  grade?: string | null;
  center: Coord | null;
  bbox: BBox | null;
  zoom?: number | null;
  org_count: number;
  manager_count: number;
  elderly_count: number;
  case_count: number;
  critical_count: number;
  monthly_matches?: number | null;
  success_rate?: number | null;
}

// 권역 색 — 시도 17개까지 인접 권역이 같은 색으로 붙지 않게 채도/색상을 흩어 둔 팔레트.
// code 기준 결정적(deterministic) 배정이라 새로고침해도 색이 바뀌지 않는다.
const REGION_PALETTE = [
  "#E8825B", "#4F7FE0", "#3FA37A", "#C561C7", "#D9A227",
  "#3FA0B8", "#E0616B", "#7B6BE0", "#68A63C", "#B8743C",
];

export function regionColorFor(code: string): string {
  let h = 0;
  for (let i = 0; i < code.length; i++) h = (h * 31 + code.charCodeAt(i)) >>> 0;
  return REGION_PALETTE[h % REGION_PALETTE.length];
}

type VisualState = "default" | "selected" | "muted";

function stateOf(region: MapRegion, selectedId: number | null): VisualState {
  if (selectedId == null) return "default";
  return region.id === selectedId ? "selected" : "muted";
}

function rectOptionsFor(color: string, state: VisualState) {
  if (state === "selected") {
    return {
      strokeColor: color, strokeStyle: "solid" as const, strokeWeight: 3,
      strokeOpacity: 0.95, fillColor: color, fillOpacity: 0.10,
    };
  }
  if (state === "muted") {
    return {
      strokeColor: color, strokeStyle: "shortdash" as const, strokeWeight: 1,
      strokeOpacity: 0.16, fillColor: color, fillOpacity: 0.012,
    };
  }
  return {
    strokeColor: color, strokeStyle: "shortdash" as const, strokeWeight: 1.6,
    strokeOpacity: 0.55, fillColor: color, fillOpacity: 0.045,
  };
}

function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, ch =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]!));
}

// 권역 라벨 pill — 권역명 + 진행 케이스 수. 클릭하면 그 권역이 선택된다.
// 마커 아이콘은 HTML이라 opacity/scale을 CSS로 직접 준다(폴리곤과 달리 setOptions 불가).
function labelHtml(region: MapRegion, color: string, state: VisualState): string {
  const selected = state === "selected";
  const opacity  = state === "muted" ? 0.3 : 1;
  const critical = region.critical_count > 0;
  return `
    <div style="
      transform:translate(-50%,-50%) scale(${selected ? 1.08 : 1});
      opacity:${opacity};
      display:inline-flex; align-items:center; gap:6px;
      padding:${selected ? "5px 11px" : "4px 9px"};
      border-radius:999px; white-space:nowrap; cursor:pointer;
      background:#ffffff; color:#20293a;
      border:${selected ? 2 : 1.2}px solid ${color};
      box-shadow:0 ${selected ? 6 : 2}px ${selected ? 16 : 6}px rgba(20,25,40,${selected ? 0.24 : 0.14});
      font-family:'Pretendard','Apple SD Gothic Neo',sans-serif;
      font-size:${selected ? 12 : 11}px; font-weight:700; letter-spacing:-0.01em;
      transition:all 0.15s;
    ">
      <span style="width:7px;height:7px;border-radius:50%;background:${color};flex:0 0 auto;"></span>
      <span>${escapeHtml(region.name)}</span>
      <span style="font-weight:600;color:#6b7684;">${region.case_count}건</span>
      ${critical ? `<span style="font-weight:700;color:#D14343;">·위험 ${region.critical_count}</span>` : ""}
    </div>`;
}

interface UseNaverRegionsArgs {
  map: any | null;
  regions: MapRegion[] | null | undefined;
  show: boolean;
  selectedId: number | null;
  onSelect?: (id: number) => void;
  // 지도 오른쪽을 덮는 패널(권역 관리) 폭. fitBounds가 이만큼을 여백으로 잡아
  // 선택한 권역이 패널 뒤에 숨지 않게 한다.
  fitRightPadding?: number;
  // 하위 구역이 선택된 상태면 카메라는 useNaverOverlays(구역 폴리곤)가 잡는다.
  // 여기서 권역 전체로 다시 fit 하면 방금 좁힌 구역이 도로 넓어진다.
  subAreaActive?: boolean;
}

export function useNaverRegions({ map, regions, show, selectedId, onSelect, fitRightPadding, subAreaActive }: UseNaverRegionsArgs): void {
  const rectsRef  = React.useRef(new Map<number, any>());
  const labelsRef = React.useRef(new Map<number, any>());
  const listenersRef = React.useRef<any[]>([]);
  const lastFitRef = React.useRef<number | "all" | null>(null);

  // 클릭 핸들러는 최신 것을 쓰되, 리스너 재등록으로 마커를 다시 만들지 않도록 ref 경유.
  const onSelectRef = React.useRef(onSelect);
  React.useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  // 패널 개폐만으로 지도를 다시 맞추지 않도록 ref로만 읽는다(fit effect의 dep 아님).
  const padRef = React.useRef(fitRightPadding);
  React.useEffect(() => { padRef.current = fitRightPadding; }, [fitRightPadding]);

  const cleanup = React.useCallback(() => {
    const naver = window.naver;
    for (const l of listenersRef.current) {
      try { naver?.maps?.Event?.removeListener(l); } catch { /* SDK 버전차 흡수 */ }
    }
    listenersRef.current = [];
    for (const [, r] of rectsRef.current)  { try { r.setMap(null); } catch {} }
    rectsRef.current.clear();
    for (const [, m] of labelsRef.current) { try { m.setMap(null); } catch {} }
    labelsRef.current.clear();
  }, []);

  // ── 생성/파기 ──────────────────────────────────────────────────
  // 권역은 최대 수십 건이라 목록이 바뀌면 통째로 다시 그린다(diff 불필요).
  React.useEffect(() => {
    if (!map || !window.naver?.maps) return;
    const naver = window.naver;
    cleanup();
    if (!show || !regions?.length) return;

    for (const region of regions) {
      const color = regionColorFor(region.code);
      const state = stateOf(region, selectedId);

      if (region.bbox && region.bbox.north !== region.bbox.south && region.bbox.east !== region.bbox.west) {
        rectsRef.current.set(region.id, new naver.maps.Rectangle({
          map,
          bounds: new naver.maps.LatLngBounds(
            new naver.maps.LatLng(region.bbox.south, region.bbox.west),
            new naver.maps.LatLng(region.bbox.north, region.bbox.east),
          ),
          ...rectOptionsFor(color, state),
          clickable: false,
          zIndex: 10,
        }));
      }

      if (region.center) {
        const marker = new naver.maps.Marker({
          map,
          position: new naver.maps.LatLng(region.center.lat, region.center.lng),
          icon: { content: labelHtml(region, color, state), anchor: new naver.maps.Point(0, 0) },
          zIndex: state === "selected" ? 320 : 300,
        });
        labelsRef.current.set(region.id, marker);
        listenersRef.current.push(
          naver.maps.Event.addListener(marker, "click", () => onSelectRef.current?.(region.id)),
        );
      }
    }

    return cleanup;
    // selectedId는 아래 restyle effect가 담당 — 여기 넣으면 선택할 때마다 전체 재생성.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, regions, show, cleanup]);

  // ── 선택 상태 반영(재도장) ─────────────────────────────────────
  React.useEffect(() => {
    if (!map || !window.naver?.maps || !regions?.length) return;
    const naver = window.naver;
    for (const region of regions) {
      const color = regionColorFor(region.code);
      const state = stateOf(region, selectedId);
      const rect = rectsRef.current.get(region.id);
      if (rect) { try { rect.setOptions(rectOptionsFor(color, state)); } catch {} }
      const label = labelsRef.current.get(region.id);
      if (label) {
        try {
          label.setIcon({ content: labelHtml(region, color, state), anchor: new naver.maps.Point(0, 0) });
          label.setZIndex(state === "selected" ? 320 : 300);
        } catch {}
      }
    }
  }, [map, regions, selectedId]);

  // ── 선택된 권역으로 이동 ───────────────────────────────────────
  // 같은 선택이 반복 렌더로 다시 들어와도 사용자가 손으로 옮긴 시점을 뺏지 않도록
  // "선택이 실제로 바뀐 순간"에만 카메라를 움직인다.
  React.useEffect(() => {
    if (!map || !window.naver?.maps || !regions?.length) return;
    const key = selectedId ?? "all";
    if (lastFitRef.current === key) return;
    const first = lastFitRef.current === null;
    lastFitRef.current = key;
    if (first) return;   // 초기 진입은 NaverMapCanvas의 bbox fit에 맡긴다.
    // 하위 구역까지 고른 이동이면 구역 폴리곤 fit(useNaverOverlays)이 우선.
    if (subAreaActive) return;

    const naver = window.naver;
    const target = selectedId == null
      ? unionBBox(regions)
      : regions.find(r => r.id === selectedId)?.bbox ?? null;
    if (!target || target.north === target.south) return;
    try {
      map.fitBounds(new naver.maps.LatLngBounds(
        new naver.maps.LatLng(target.south, target.west),
        new naver.maps.LatLng(target.north, target.east),
      ), { top: 70, right: 70, bottom: 70, left: 70 });
    } catch { /* 줌 한계 등은 무시 */ }

    // 네이버 SDK의 fitBounds margin은 줌 계산에만 반영되고 중심은 늘 bounds 중앙이라,
    // 오른쪽을 덮는 패널만큼은 투영 좌표로 직접 밀어 준다(안 그러면 선택한 권역이
    // 패널 뒤에 숨는다). 줌은 그대로 두고 중심만 옮긴다.
    const pad = padRef.current ?? 0;
    if (pad > 70) {
      try {
        const proj = map.getProjection();
        const p = proj.fromCoordToOffset(map.getCenter());
        map.setCenter(proj.fromOffsetToCoord(new naver.maps.Point(p.x + (pad - 70) / 2, p.y)));
      } catch (e) { console.error("[useNaverRegions] pan for panel failed", e); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, regions, selectedId]);   // subAreaActive는 조건 판단용 — dep에 넣으면 구역 해제 시 중복 fit
}

// "전체 보기"용 — 모든 권역 bbox의 합집합.
function unionBBox(regions: MapRegion[]): BBox | null {
  let acc: BBox | null = null;
  for (const r of regions) {
    if (!r.bbox) continue;
    if (!acc) { acc = { ...r.bbox }; continue; }
    acc.north = Math.max(acc.north, r.bbox.north);
    acc.south = Math.min(acc.south, r.bbox.south);
    acc.east  = Math.max(acc.east,  r.bbox.east);
    acc.west  = Math.min(acc.west,  r.bbox.west);
  }
  return acc;
}
